// Pure presentation helpers for the admin continuing-review queue.
//
// Kept framework-free so they can be unit tested directly and reused across the
// queue table and its modals.

/** Queue states that make up the default "active" work list. */
export const ACTIVE_QUEUE_STATES = [
  'DUE_SOON',
  'DUE_TODAY',
  'LAPSED',
  'ACTIVE_CR_PENDING',
] as const;

/** States returned by the default (no includeStates) queue RPC. */
export const DEFAULT_QUEUE_STATES = [
  ...ACTIVE_QUEUE_STATES,
  'METADATA_REQUIRED',
] as const;

/** Order of the filter chips shown above the table. */
export const CHIP_STATES = [
  'DUE_SOON',
  'DUE_TODAY',
  'LAPSED',
  'ACTIVE_CR_PENDING',
  'METADATA_REQUIRED',
  'NOT_REQUIRED',
] as const;

export const WINDOW_DAY_OPTIONS = [30, 60, 90] as const;
export const DEFAULT_WINDOW_DAYS = 60;

export interface QueueStateMeta {
  /** Short human label used in chips and badges. */
  label: string;
  /** Tailwind classes for the colour-coded badge. */
  badgeClass: string;
}

export const QUEUE_STATE_META: Record<string, QueueStateMeta> = {
  DUE_SOON: { label: 'Due soon', badgeClass: 'bg-amber-100 text-amber-800' },
  DUE_TODAY: { label: 'Due today', badgeClass: 'bg-orange-100 text-orange-800' },
  LAPSED: { label: 'Lapsed', badgeClass: 'bg-red-100 text-red-800' },
  ACTIVE_CR_PENDING: { label: 'CR in progress', badgeClass: 'bg-blue-100 text-blue-800' },
  METADATA_REQUIRED: { label: 'Metadata required', badgeClass: 'bg-gray-200 text-gray-800' },
  NOT_REQUIRED: { label: 'Not required', badgeClass: 'bg-green-100 text-green-800' },
  NOT_DUE: { label: 'Not due', badgeClass: 'bg-gray-100 text-gray-600' },
  SUPERSEDED: { label: 'Superseded', badgeClass: 'bg-gray-100 text-gray-600' },
  INELIGIBLE: { label: 'Ineligible', badgeClass: 'bg-gray-100 text-gray-600' },
};

export const stateMeta = (state: string): QueueStateMeta =>
  QUEUE_STATE_META[state] ?? { label: state, badgeClass: 'bg-gray-100 text-gray-600' };

/** Controlled "not required" reasons accepted by the remediation RPC. */
export const NOT_REQUIRED_REASONS: Array<{ value: string; label: string }> = [
  { value: 'HHS_ELIGIBLE_EXPEDITED', label: 'HHS — eligible for expedited review' },
  { value: 'HHS_LIMITED_IRB_REVIEW', label: 'HHS — limited IRB review' },
  { value: 'HHS_DATA_ANALYSIS_ONLY', label: 'HHS — data analysis only' },
  { value: 'HHS_FOLLOWUP_CLINICAL_DATA_ONLY', label: 'HHS — follow-up of clinical data only' },
  { value: 'IRB_DETERMINED_NOT_REQUIRED', label: 'IRB determined not required' },
];

/** Reminder send is meaningless for these states. */
export const REMINDER_DISABLED_STATES = new Set(['METADATA_REQUIRED', 'NOT_REQUIRED']);

export interface DaysUntilExpiryDisplay {
  text: string;
  /** true when the study is past expiry (negative days). */
  overdue: boolean;
}

/** Format days-until-expiry; negatives render as "overdue Nd". */
export const formatDaysUntilExpiry = (days: number | null | undefined): DaysUntilExpiryDisplay => {
  if (days === null || days === undefined) {
    return { text: '—', overdue: false };
  }
  if (days < 0) {
    return { text: `overdue ${Math.abs(days)}d`, overdue: true };
  }
  if (days === 0) {
    return { text: 'today', overdue: false };
  }
  return { text: `${days}d`, overdue: false };
};

/** Compact relative time, e.g. "just now", "5m ago", "3h ago", "2d ago". */
export const relativeTimeFrom = (iso: string | null | undefined, now: Date = new Date()): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = now.getTime() - then;
  if (diffMs < 0) return 'just now';
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
};

/** Format an ISO date as a short calendar date (no time). Empty when missing. */
export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (value: string): boolean => EMAIL_RE.test(value.trim());

export interface ParsedCcEmails {
  emails: string[];
  invalid: string[];
}

/** Split a comma-separated CC string and report any invalid addresses. */
export const parseCcEmails = (raw: string): ParsedCcEmails => {
  const emails = raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const invalid = emails.filter((email) => !isValidEmail(email));
  return { emails, invalid };
};

/** Reminder status badge styling. */
export const reminderStatusBadgeClass = (status: string | null | undefined): string => {
  switch ((status ?? '').toUpperCase()) {
    case 'SENT':
      return 'bg-green-100 text-green-800';
    case 'FAILED':
      return 'bg-red-100 text-red-800';
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

/** Hint about which reminder template the server will pick for this state. */
export const reminderTemplateHint = (queueState: string): string =>
  queueState === 'LAPSED'
    ? 'Lapsed template — the study has passed its expiry date.'
    : 'Due-soon template — the study is approaching its expiry date.';

export const MAX_CUSTOM_MESSAGE_LENGTH = 2000;
export const MIN_INTERVAL_MONTHS = 1;
export const MAX_INTERVAL_MONTHS = 60;
export const FDA_MAX_INTERVAL_MONTHS = 12;

/** FDA-authority basis codes cap the review interval at 12 months. */
export const hasFdaBasisCode = (codes: string[]): boolean =>
  codes.some((code) => code.toUpperCase().startsWith('FDA_'));
